import type { z } from 'zod';
import { apiErrorSchema } from '@pm/contracts';
import { csrfResponseSchema } from '../features/auth/schemas.js';
import { formatMoney as formatMoneyForLocale } from '../i18n.js';

let csrfToken: string | undefined;
let csrfTokenRequest: Promise<string> | undefined;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  schema?: z.ZodType<T>,
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const requiresCsrf = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  if (requiresCsrf) await getCsrfToken();

  let requestToken = csrfToken;
  let response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
      ...init.headers,
    },
  });
  if (requiresCsrf && (await isInvalidCsrfResponse(response))) {
    await refreshCsrfToken(requestToken);
    requestToken = csrfToken;
    response = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(requestToken ? { 'x-csrf-token': requestToken } : {}),
        ...init.headers,
      },
    });
  }
  if (response.status === 204) return undefined as T;
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    throw new ApiError(
      response.status,
      'invalid_response',
      'The API returned an invalid response.',
    );
  }
  if (!response.ok) {
    const errorPayload = apiErrorSchema.safeParse(payload);
    throw new ApiError(
      response.status,
      errorPayload.success ? errorPayload.data.error.code : 'request_failed',
      errorPayload.success ? errorPayload.data.error.message : 'The request failed.',
    );
  }
  if (schema) {
    const parsed = schema.safeParse(payload);
    if (!parsed.success)
      throw new ApiError(
        response.status,
        'invalid_response',
        'The API returned an invalid response.',
      );
    return parsed.data;
  }
  return payload as T;
}

async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  csrfTokenRequest ??= fetchCsrfToken();
  try {
    csrfToken = await csrfTokenRequest;
    return csrfToken;
  } finally {
    csrfTokenRequest = undefined;
  }
}

async function fetchCsrfToken(): Promise<string> {
  const tokenResponse = await fetch('/api/v1/auth/csrf', { credentials: 'include' });
  if (!tokenResponse.ok)
    throw new ApiError(
      tokenResponse.status,
      'csrf_unavailable',
      'Unable to initialize request security.',
    );
  let tokenPayload: unknown;
  try {
    tokenPayload = await tokenResponse.json();
  } catch {
    throw new ApiError(
      tokenResponse.status,
      'invalid_response',
      'The API returned an invalid response.',
    );
  }
  const parsedToken = csrfResponseSchema.safeParse(tokenPayload);
  if (!parsedToken.success)
    throw new ApiError(
      tokenResponse.status,
      'invalid_response',
      'The API returned an invalid response.',
    );
  return parsedToken.data.csrfToken;
}

async function refreshCsrfToken(rejectedToken: string | undefined): Promise<string> {
  if (csrfToken && csrfToken !== rejectedToken) return csrfToken;
  csrfToken = undefined;
  return getCsrfToken();
}

async function isInvalidCsrfResponse(response: Response): Promise<boolean> {
  if (response.status !== 403) return false;
  try {
    const payload: unknown = await response.clone().json();
    const parsed = apiErrorSchema.safeParse(payload);
    return parsed.success && parsed.data.error?.code === 'invalid_csrf_token';
  } catch {
    return false;
  }
}

export function formatMoney(amountMinor: number, currency: string, language?: string): string {
  return formatMoneyForLocale(amountMinor, currency, language);
}
